import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { Lock, Menu, X } from "lucide-react";
import { SEMPLIFICAPP_URL } from "@/data/content";

const links = [
  { label: "Vantaggi", href: "#vantaggi" },
  { label: "Come funziona", href: "#come-funziona" },
  { label: "Costi", href: "#costi" },
  { label: "Blog", href: "#blog" },
  { label: "Chi siamo", href: "#chi-siamo" },
  { label: "FAQ", href: "#faq" },
];

export const scrollToId = (hash) => {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const solid = scrolled || pathname !== "/" || mobileOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const go = (hash) => {
    setMobileOpen(false);
    if (pathname !== "/") {
      sessionStorage.setItem("scrollTo", hash);
      navigate("/");
      return;
    }
    setTimeout(() => scrollToId(hash), 150);
  };

  return (
    <motion.header
      data-testid="main-nav-container"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        solid
          ? "bg-ink/85 backdrop-blur-xl border-b border-white/10 shadow-lg"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between">
        <a
          href="/"
          data-testid="nav-logo-link"
          onClick={(e) => {
            e.preventDefault();
            setMobileOpen(false);
            if (pathname !== "/") navigate("/");
            else window.__lenis?.scrollTo(0);
          }}
          className="flex items-center"
        >
          <img
            src="/assets/logo-nuovo.png"
            alt="Semplificaffitto.it"
            className="h-7 sm:h-8 w-auto brightness-0 invert"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={(e) => {
                e.preventDefault();
                go(l.href);
              }}
              className="text-sm text-white/70 hover:text-white transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#contatti"
            data-testid="nav-contact-cta-btn"
            onClick={(e) => {
              e.preventDefault();
              go("#contatti");
            }}
            className="hidden sm:inline-flex text-sm font-medium text-white/90 border border-white/25 rounded-full px-5 py-2.5 hover:bg-white/10 transition-colors duration-300"
          >
            Contattaci
          </a>
          <a
            href={SEMPLIFICAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-reserved-area-btn"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold bg-azure hover:bg-brandblue text-white rounded-full px-5 py-2.5 transition-colors duration-300"
          >
            <Lock size={14} strokeWidth={2.5} />
            Area Riservata
          </a>
          <button
            data-testid="nav-mobile-menu-btn"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Chiudi menu" : "Apri menu"}
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/20 text-white hover:bg-white/10 transition-colors duration-300"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-testid="nav-mobile-drawer"
            className="lg:hidden overflow-hidden bg-ink/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {links.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={(e) => {
                    e.preventDefault();
                    go(l.href);
                  }}
                  className="py-3.5 text-lg font-serif text-white/80 hover:text-azure border-b border-white/5 transition-colors duration-300"
                >
                  {l.label}
                </motion.a>
              ))}
              <div className="flex flex-col gap-3 pt-5">
                <a
                  href="#contatti"
                  data-testid="nav-mobile-contact-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    go("#contatti");
                  }}
                  className="inline-flex justify-center items-center gap-2 bg-gold text-ink font-semibold rounded-full px-6 py-3.5 text-sm"
                >
                  Valuta il tuo immobile gratis
                </a>
                <a
                  href={SEMPLIFICAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-mobile-reserved-btn"
                  className="inline-flex justify-center items-center gap-2 border border-white/25 text-white rounded-full px-6 py-3.5 text-sm font-semibold"
                >
                  <Lock size={14} />
                  Area Riservata — Semplificapp.it
                </a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
